// src/components/pcr/sections/PrimaryAssessment.tsx
import React, { useEffect, useRef } from 'react';
import { useForm } from '../../../contexts/FormContext';
import SectionHeading from '../common/SectionHeading';
import TextareaField from '../common/TextareaField';
import { toggleValue, toggleNumericValue } from '../../../utils/formUtils';

interface PrimaryAssessmentProps {
  sectionHighlight?: string;
}

const PrimaryAssessment: React.FC<PrimaryAssessmentProps> = ({ sectionHighlight }) => {
  const { formData, updateFormField } = useForm();
  const { primaryAssessment } = formData;

  const handleTextChange = (field: string, value: string) => {
    updateFormField('primaryAssessment', field, value);
  };

  const handleAVPUChange = (value: string) => {
    // Toggle the value
    const newValue = toggleValue(primaryAssessment.LOCMentalStatus.AVPU, value);
    
    updateFormField('primaryAssessment', 'LOCMentalStatus', {
      ...primaryAssessment.LOCMentalStatus,
      AVPU: newValue
    });
  };

  const handleOrientationChange = (value: string, checked: boolean) => {
    const currentOrientation = primaryAssessment.LOCMentalStatus.orientation || [];
    
    let newOrientation;
    if (checked) {
      // Add to orientation
      newOrientation = [...currentOrientation, value];
    } else {
      // Remove from orientation
      newOrientation = currentOrientation.filter(item => item !== value);
    }
    
    updateFormField('primaryAssessment', 'LOCMentalStatus', {
      ...primaryAssessment.LOCMentalStatus,
      orientation: newOrientation
    });
  };

  const handleOrientationChanges = (values: string[]) => {
    updateFormField('primaryAssessment', 'LOCMentalStatus', {
      ...primaryAssessment.LOCMentalStatus,
      orientation: values
    });
  };

  const handleSkinChange = (category: 'color' | 'temperature' | 'condition', value: string) => {
    // Toggle the value
    const newValue = toggleValue(primaryAssessment.circulationAssessment.skin[category], value);
    
    updateFormField('primaryAssessment', 'circulationAssessment', {
      ...primaryAssessment.circulationAssessment,
      skin: {
        ...primaryAssessment.circulationAssessment.skin,
        [category]: newValue
      }
    });
  };

  const handlePulseChange = (category: 'rate' | 'rhythm' | 'quality', value: string) => {
    // Toggle the value
    const newValue = toggleValue(primaryAssessment.circulationAssessment.pulse[category], value);
    
    updateFormField('primaryAssessment', 'circulationAssessment', {
      ...primaryAssessment.circulationAssessment,
      pulse: {
        ...primaryAssessment.circulationAssessment.pulse,
        [category]: newValue
      }
    });
  };

  const handleGCSChange = (category: 'eyeOpening' | 'verbalResponse' | 'motorResponse', value: number) => {
    // Toggle the value
    const newValue = toggleNumericValue(primaryAssessment.GCS[category], value);
    
    const updatedGCS = {
      ...primaryAssessment.GCS,
      [category]: newValue
    };
    
    // Calculate total score
    updatedGCS.totalScore = 
      (updatedGCS.eyeOpening || 0) + 
      (updatedGCS.verbalResponse || 0) + 
      (updatedGCS.motorResponse || 0);
    
    updateFormField('primaryAssessment', 'GCS', updatedGCS);
  };

  const chiefComplaintRef = useRef<HTMLDivElement>(null);
  const mentalStatusRef = useRef<HTMLDivElement>(null);
  const airwayAssessmentRef = useRef<HTMLDivElement>(null);
  const breathingAssessmentRef = useRef<HTMLDivElement>(null);
  const circulationAssessmentRef = useRef<HTMLDivElement>(null);
  const gcsRef = useRef<HTMLDivElement>(null);
  const patientPriorityRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!sectionHighlight) return;
    
    // Find the right section to highlight
    let sectionRef: any = null;
    let elementId: string | null = null;
    
    switch(sectionHighlight) {
      case 'chief-complaint':
        sectionRef = chiefComplaintRef;
        elementId = 'PrimaryAssessment-chief-complaint';
        break;
      case 'mental-status':
        sectionRef = mentalStatusRef;
        elementId = 'loc-avpu-a';
        break;
      case 'airway-assessment':
        sectionRef = airwayAssessmentRef;
        elementId = 'PrimaryAssessment-airway-assessment';
        break;
      case 'breathing-assessment':
        sectionRef = breathingAssessmentRef;
        elementId = 'PrimaryAssessment-breathing-assessment';
        break;
      case 'circulation-assessment':
        sectionRef = circulationAssessmentRef;
        elementId = 'skin-parameters';
        break;
      case 'gcs':
        sectionRef = gcsRef;
        elementId = 'PrimaryAssessment-gcs-total-score';
        break;
      case 'patient-priority':
        sectionRef = patientPriorityRef;
        elementId = 'PrimaryAssessment-decision-(pt.-priority)';
        break;
    }
    
    if (sectionRef?.current) {
      // Add a highlight class
      sectionRef.current.classList.add('section-highlight');
      
      // Scroll the section into view
      sectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      
      // Alternatively, find the specific element and highlight it
      if (elementId) {
        const element = document.getElementById(elementId);
        if (element) {
          const originalBg = element.style.backgroundColor;
          element.style.backgroundColor = 'rgba(59, 130, 246, 0.2)'; // Light blue highlight
          
          // Restore original background after 2 seconds
          setTimeout(() => {
            element.style.backgroundColor = originalBg;
          }, 2000);
        }
      }
      
      // Remove highlight class after animation
      setTimeout(() => {
        if (sectionRef?.current) {
          sectionRef.current.classList.remove('section-highlight');
        }
      }, 2000);
    }
  }, [sectionHighlight]);

  const avpuOptions = [
    { id: 'avpu-a', label: 'A', value: 'A' },
    { id: 'avpu-v', label: 'V', value: 'V' },
    { id: 'avpu-p', label: 'P', value: 'P' },
    { id: 'avpu-u', label: 'U', value: 'U' }
  ];

  const eyeOpeningOptions = [
    { id: 'gcs-eye-4', label: '4. Spontaneously', value: '4' },
    { id: 'gcs-eye-3', label: '3. To speech', value: '3' },
    { id: 'gcs-eye-2', label: '2. To pain', value: '2' },
    { id: 'gcs-eye-1', label: '1. No response', value: '1' }
  ];

  const orientationOptions = [
    { id: 'orientation-person', label: 'Person', value: 'Person' },
    { id: 'orientation-place', label: 'Place', value: 'Place' },
    { id: 'orientation-time', label: 'Time', value: 'Time' },
    { id: 'orientation-cc', label: 'C/C', value: 'C/C' }
  ];

  return (
    <div className="bg-gradient-to-br from-emerald-50 to-white p-4 rounded-lg mb-6 shadow-md">
      <SectionHeading 
        title="Primary Assessment" 
        description="You must demonstrate your understanding of the fundamentals of the primary assessment by hitting all the critical points, while being concise."
      />
      
      <div className="space-y-3">
        <div className="space-y-4">
          <TextareaField
            id="PrimaryAssessment-general-impression"
            label="General Impression"
            value={primaryAssessment.generalImpression}
            onChange={(e) => handleTextChange('generalImpression', e.target.value)}
          />
          <div ref={mentalStatusRef} className={`bg-white p-4 rounded-lg shadow-sm ${sectionHighlight === 'mental-status' ? 'ring-2 ring-blue-500' : ''}`}>
            <h3 className="text-md font-medium text-gray-700 mb-3">LOC + Mental Status</h3>
            
            {/* AVPU Grid */}
            <div className="bg-gray-50 p-3 rounded-lg shadow-md mb-4">
              <div className="grid grid-cols-4 gap-4 mb-2">
                <div className="text-center">
                  <div className="text-sm font-medium text-gray-700">A</div>
                </div>
                <div className="text-center">
                  <div className="text-sm font-medium text-gray-700">V</div>
                </div>
                <div className="text-center">
                  <div className="text-sm font-medium text-gray-700">P</div>
                </div>
                <div className="text-center">
                  <div className="text-sm font-medium text-gray-700">U</div>
                </div>
              </div>
              <div className="border rounded-lg overflow-hidden bg-white">
                <div className="grid grid-cols-4">
                  <div className="border-r px-4 py-2">
                    <div className="flex justify-center">
                      <input 
                        type="checkbox" 
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        checked={primaryAssessment.LOCMentalStatus.AVPU === 'A'}
                        onChange={() => handleAVPUChange('A')}
                        id="loc-avpu-a"
                      />
                    </div>
                  </div>
                  <div className="border-r px-4 py-2">
                    <div className="flex justify-center">
                      <input 
                        type="checkbox" 
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        checked={primaryAssessment.LOCMentalStatus.AVPU === 'V'}
                        onChange={() => handleAVPUChange('V')}
                        id="loc-avpu-v"
                      />
                    </div>
                  </div>
                  <div className="border-r px-4 py-2">
                    <div className="flex justify-center">
                      <input 
                        type="checkbox" 
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        checked={primaryAssessment.LOCMentalStatus.AVPU === 'P'}
                        onChange={() => handleAVPUChange('P')}
                        id="loc-avpu-p"
                      />
                    </div>
                  </div>
                  <div className="px-4 py-2">
                    <div className="flex justify-center">
                      <input 
                        type="checkbox" 
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        checked={primaryAssessment.LOCMentalStatus.AVPU === 'U'}
                        onChange={() => handleAVPUChange('U')}
                        id="loc-avpu-u"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Orientation Grid */}
            <div className="bg-gray-50 p-3 rounded-lg shadow-md">
              <div className="grid grid-cols-4 gap-4 mb-2">
                <div className="text-center">
                  <div className="text-sm font-medium text-gray-700">Person</div>
                </div>
                <div className="text-center">
                  <div className="text-sm font-medium text-gray-700">Place</div>
                </div>
                <div className="text-center">
                  <div className="text-sm font-medium text-gray-700">Time</div>
                </div>
                <div className="text-center">
                  <div className="text-sm font-medium text-gray-700">Event</div>
                </div>
              </div>
              <div className="border rounded-lg overflow-hidden bg-white">
                <div className="grid grid-cols-4">
                  <div className="border-r px-4 py-2">
                    <div className="flex justify-center">
                      <input 
                        type="checkbox" 
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        checked={primaryAssessment.LOCMentalStatus.orientation?.includes('Person') || false}
                        onChange={(e) => handleOrientationChange('Person', e.target.checked)}
                        id="loc-orientation-person"
                      />
                    </div>
                  </div>
                  <div className="border-r px-4 py-2">
                    <div className="flex justify-center">
                      <input 
                        type="checkbox" 
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        checked={primaryAssessment.LOCMentalStatus.orientation?.includes('Place') || false}
                        onChange={(e) => handleOrientationChange('Place', e.target.checked)}
                        id="loc-orientation-place"
                      />
                    </div>
                  </div>
                  <div className="border-r px-4 py-2">
                    <div className="flex justify-center">
                      <input 
                        type="checkbox" 
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        checked={primaryAssessment.LOCMentalStatus.orientation?.includes('Time') || false}
                        onChange={(e) => handleOrientationChange('Time', e.target.checked)}
                        id="loc-orientation-time"
                      />
                    </div>
                  </div>
                  <div className="px-4 py-2">
                    <div className="flex justify-center">
                      <input 
                        type="checkbox" 
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        checked={primaryAssessment.LOCMentalStatus.orientation?.includes('Event') || false}
                        onChange={(e) => handleOrientationChange('Event', e.target.checked)}
                        id="loc-orientation-event"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div ref={chiefComplaintRef} className={`${sectionHighlight === 'chief-complaint' ? 'ring-2 ring-blue-500' : ''}`}>
            <TextareaField
              id="PrimaryAssessment-chief-complaint"
              label="Chief Complaint"
              value={primaryAssessment.chiefComplaint}
              onChange={(e) => handleTextChange('chiefComplaint', e.target.value)}
            />
          </div>
          
          <div ref={airwayAssessmentRef} className={`${sectionHighlight === 'airway-assessment' ? 'ring-2 ring-blue-500' : ''}`}>
            <TextareaField
              id="PrimaryAssessment-airway-assessment"
              label="Airway Assessment"
              value={primaryAssessment.airwayAssessment}
              onChange={(e) => handleTextChange('airwayAssessment', e.target.value)}
            />
          </div>
          
          <div ref={breathingAssessmentRef} className={`${sectionHighlight === 'breathing-assessment' ? 'ring-2 ring-blue-500' : ''}`}>
            <TextareaField
              id="PrimaryAssessment-breathing-assessment"
              label="Breathing Assessment"
              value={primaryAssessment.breathingAssessment}
              onChange={(e) => handleTextChange('breathingAssessment', e.target.value)}
            />
          </div>
          
          <div ref={circulationAssessmentRef} className={`bg-white p-4 rounded-lg shadow-sm ${sectionHighlight === 'circulation-assessment' ? 'ring-2 ring-blue-500' : ''}`}>
            <div className="flex justify-between items-center mb-1">
              <label htmlFor="PrimaryAssessment-circulation-assessment" className="block text-sm font-medium text-gray-700">
                Circulation Assessment
              </label>
            </div>
            
            <div className="p-4 sm:p-6 md:p-8 space-y-4">
              {/* Skin Parameters */}
<div className="bg-gray-50 p-3 rounded-lg shadow-md">
  <h4 id="skin-parameters" className="text-base font-medium text-gray-700 mb-2 text-center">Skin</h4>
  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" aria-labelledby="skin-parameters">
    {/* Skin Color */}
    <div className="bg-white p-2 rounded shadow-sm">
      <h5 id="skin-color" className="text-sm font-medium text-gray-600 mb-2 text-center">Color</h5>
      <div className="grid grid-cols-2 gap-1" aria-labelledby="skin-color">
        <label className="flex items-center space-x-2 p-1 hover:bg-gray-50 rounded" htmlFor="PrimaryAssessment-circulation-assessment-skin-0-0">
          <input
            id="PrimaryAssessment-circulation-assessment-skin-0-0"
            type="checkbox"
            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            checked={primaryAssessment.circulationAssessment.skin.color === 'Pale'}
            onChange={() => handleSkinChange('color', 'Pale')}
          />
          <span className="text-xs text-gray-700">Pale</span>
        </label>
        <label className="flex items-center space-x-2 p-1 hover:bg-gray-50 rounded" htmlFor="PrimaryAssessment-circulation-assessment-skin-0-1">
          <input
            id="PrimaryAssessment-circulation-assessment-skin-0-1"
            type="checkbox"
            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            checked={primaryAssessment.circulationAssessment.skin.color === 'Flushed'}
            onChange={() => handleSkinChange('color', 'Flushed')}
          />
          <span className="text-xs text-gray-700">Flushed</span>
        </label>
        <label className="flex items-center space-x-2 p-1 hover:bg-gray-50 rounded" htmlFor="PrimaryAssessment-circulation-assessment-skin-0-2">
          <input
            id="PrimaryAssessment-circulation-assessment-skin-0-2"
            type="checkbox"
            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            checked={primaryAssessment.circulationAssessment.skin.color === 'Cyanotic'}
            onChange={() => handleSkinChange('color', 'Cyanotic')}
          />
          <span className="text-xs text-gray-700">Cyanotic</span>
        </label>
        <label className="flex items-center space-x-2 p-1 hover:bg-gray-50 rounded" htmlFor="PrimaryAssessment-circulation-assessment-skin-0-3">
          <input
            id="PrimaryAssessment-circulation-assessment-skin-0-3"
            type="checkbox"
            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            checked={primaryAssessment.circulationAssessment.skin.color === 'Normal'}
            onChange={() => handleSkinChange('color', 'Normal')}
          />
          <span className="text-xs text-gray-700">Normal</span>
        </label>
      </div>
    </div>
    
    {/* Skin Temperature */}
    <div className="bg-white p-2 rounded shadow-sm">
      <h5 id="skin-temperature" className="text-sm font-medium text-gray-600 mb-2 text-center">Temperature</h5>
      <div className="grid grid-cols-2 gap-1" aria-labelledby="skin-temperature">
        <label className="flex items-center space-x-2 p-1 hover:bg-gray-50 rounded" htmlFor="PrimaryAssessment-circulation-assessment-skin-1-0">
          <input
            id="PrimaryAssessment-circulation-assessment-skin-1-0"
            type="checkbox"
            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            checked={primaryAssessment.circulationAssessment.skin.temperature === 'Hot'}
            onChange={() => handleSkinChange('temperature', 'Hot')}
          />
          <span className="text-xs text-gray-700">Hot</span>
        </label>
        <label className="flex items-center space-x-2 p-1 hover:bg-gray-50 rounded" htmlFor="PrimaryAssessment-circulation-assessment-skin-1-1">
          <input
            id="PrimaryAssessment-circulation-assessment-skin-1-1"
            type="checkbox"
            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            checked={primaryAssessment.circulationAssessment.skin.temperature === 'Warm'}
            onChange={() => handleSkinChange('temperature', 'Warm')}
          />
          <span className="text-xs text-gray-700">Warm</span>
        </label>
        <label className="flex items-center space-x-2 p-1 hover:bg-gray-50 rounded" htmlFor="PrimaryAssessment-circulation-assessment-skin-1-2">
          <input
            id="PrimaryAssessment-circulation-assessment-skin-1-2"
            type="checkbox"
            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            checked={primaryAssessment.circulationAssessment.skin.temperature === 'Cool'}
            onChange={() => handleSkinChange('temperature', 'Cool')}
          />
          <span className="text-xs text-gray-700">Cool</span>
        </label>
        <label className="flex items-center space-x-2 p-1 hover:bg-gray-50 rounded" htmlFor="PrimaryAssessment-circulation-assessment-skin-1-3">
          <input
            id="PrimaryAssessment-circulation-assessment-skin-1-3"
            type="checkbox"
            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            checked={primaryAssessment.circulationAssessment.skin.temperature === 'Cold'}
            onChange={() => handleSkinChange('temperature', 'Cold')}
          />
          <span className="text-xs text-gray-700">Cold</span>
        </label>
      </div>
    </div>
    
    {/* Skin Condition */}
    <div className="bg-white p-2 rounded shadow-sm">
      <h5 id="skin-condition" className="text-sm font-medium text-gray-600 mb-2 text-center">Condition</h5>
      <div className="grid grid-cols-2 gap-1" aria-labelledby="skin-condition">
        <label className="flex items-center space-x-2 p-1 hover:bg-gray-50 rounded" htmlFor="PrimaryAssessment-circulation-assessment-skin-2-0">
          <input
            id="PrimaryAssessment-circulation-assessment-skin-2-0"
            type="checkbox"
            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            checked={primaryAssessment.circulationAssessment.skin.condition === 'Dry'}
            onChange={() => handleSkinChange('condition', 'Dry')}
          />
          <span className="text-xs text-gray-700">Dry</span>
        </label>
        <label className="flex items-center space-x-2 p-1 hover:bg-gray-50 rounded" htmlFor="PrimaryAssessment-circulation-assessment-skin-2-1">
          <input
            id="PrimaryAssessment-circulation-assessment-skin-2-1"
            type="checkbox"
            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            checked={primaryAssessment.circulationAssessment.skin.condition === 'Diaphoretic'}
            onChange={() => handleSkinChange('condition', 'Diaphoretic')}
          />
          <span className="text-xs text-gray-700">Diaphoretic</span>
        </label>
        <label className="flex items-center space-x-2 p-1 hover:bg-gray-50 rounded" htmlFor="PrimaryAssessment-circulation-assessment-skin-2-2">
          <input
            id="PrimaryAssessment-circulation-assessment-skin-2-2"
            type="checkbox"
            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            checked={primaryAssessment.circulationAssessment.skin.condition === 'Moist'}
            onChange={() => handleSkinChange('condition', 'Moist')}
          />
          <span className="text-xs text-gray-700">Moist</span>
        </label>
        <label className="flex items-center space-x-2 p-1 hover:bg-gray-50 rounded" htmlFor="PrimaryAssessment-circulation-assessment-skin-2-3">
          <input
            id="PrimaryAssessment-circulation-assessment-skin-2-3"
            type="checkbox"
            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            checked={primaryAssessment.circulationAssessment.skin.condition === 'Normal'}
            onChange={() => handleSkinChange('condition', 'Normal')}
          />
          <span className="text-xs text-gray-700">Normal</span>
        </label>
      </div>
    </div>
  </div>
</div>
              
  {/* Pulse Parameters */}
<div className="bg-gray-50 p-3 rounded-lg shadow-md">
  <h4 id="pulse-parameters" className="text-base font-medium text-gray-700 mb-2 text-center">Pulse</h4>
  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" aria-labelledby="pulse-parameters">
    {/* Pulse Rate */}
    <div className="bg-white p-2 rounded shadow-sm">
      <h5 id="pulse-rate" className="text-sm font-medium text-gray-600 mb-2 text-center">Rate</h5>
      <div className="grid grid-cols-2 sm:grid-cols-2 gap-1" aria-labelledby="pulse-rate">
        <label className="flex items-center space-x-2 p-1 hover:bg-gray-50 rounded" htmlFor="PrimaryAssessment-circulation-assessment-pulse-0-0">
          <input
            id="PrimaryAssessment-circulation-assessment-pulse-0-0"
            type="checkbox"
            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            checked={primaryAssessment.circulationAssessment.pulse.rate === 'Normal'}
            onChange={() => handlePulseChange('rate', 'Normal')}
          />
          <span className="text-xs text-gray-700">Normal</span>
        </label>
        <label className="flex items-center space-x-2 p-1 hover:bg-gray-50 rounded" htmlFor="PrimaryAssessment-circulation-assessment-pulse-0-1">
          <input
            id="PrimaryAssessment-circulation-assessment-pulse-0-1"
            type="checkbox"
            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            checked={primaryAssessment.circulationAssessment.pulse.rate === 'Bradycardic'}
            onChange={() => handlePulseChange('rate', 'Bradycardic')}
          />
          <span className="text-xs text-gray-700">Bradycardic</span>
        </label>
        <label className="flex items-center space-x-2 p-1 hover:bg-gray-50 rounded" htmlFor="PrimaryAssessment-circulation-assessment-pulse-0-2">
          <input
            id="PrimaryAssessment-circulation-assessment-pulse-0-2"
            type="checkbox"
            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            checked={primaryAssessment.circulationAssessment.pulse.rate === 'Tachycardic'}
            onChange={() => handlePulseChange('rate', 'Tachycardic')}
          />
          <span className="text-xs text-gray-700">Tachycardic</span>
        </label>
      </div>
    </div>
    
    {/* Pulse Rhythm */}
    <div className="bg-white p-2 rounded shadow-sm">
      <h5 id="pulse-rhythm" className="text-sm font-medium text-gray-600 mb-2 text-center">Rhythm</h5>
      <div className="grid grid-cols-2 sm:grid-cols-2 gap-1" aria-labelledby="pulse-rhythm">
        <label className="flex items-center space-x-2 p-1 hover:bg-gray-50 rounded" htmlFor="PrimaryAssessment-circulation-assessment-pulse-1-0">
          <input
            id="PrimaryAssessment-circulation-assessment-pulse-1-0"
            type="checkbox"
            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            checked={primaryAssessment.circulationAssessment.pulse.rhythm === 'Regular'}
            onChange={() => handlePulseChange('rhythm', 'Regular')}
          />
          <span className="text-xs text-gray-700">Regular</span>
        </label>
        <label className="flex items-center space-x-2 p-1 hover:bg-gray-50 rounded" htmlFor="PrimaryAssessment-circulation-assessment-pulse-1-1">
          <input
            id="PrimaryAssessment-circulation-assessment-pulse-1-1"
            type="checkbox"
            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            checked={primaryAssessment.circulationAssessment.pulse.rhythm === 'Irregular'}
            onChange={() => handlePulseChange('rhythm', 'Irregular')}
          />
          <span className="text-xs text-gray-700">Irregular</span>
        </label>
      </div>
    </div>
    
    {/* Pulse Quality */}
    <div className="bg-white p-2 rounded shadow-sm">
      <h5 id="pulse-quality" className="text-sm font-medium text-gray-600 mb-2 text-center">Quality</h5>
      <div className="grid grid-cols-2 gap-1" aria-labelledby="pulse-quality">
        <label className="flex items-center space-x-2 p-1 hover:bg-gray-50 rounded" htmlFor="PrimaryAssessment-circulation-assessment-pulse-2-0">
          <input
            id="PrimaryAssessment-circulation-assessment-pulse-2-0"
            type="checkbox"
            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            checked={primaryAssessment.circulationAssessment.pulse.quality === 'Strong'}
            onChange={() => handlePulseChange('quality', 'Strong')}
          />
          <span className="text-xs text-gray-700">Strong</span>
        </label>
        <label className="flex items-center space-x-2 p-1 hover:bg-gray-50 rounded" htmlFor="PrimaryAssessment-circulation-assessment-pulse-2-1">
          <input
            id="PrimaryAssessment-circulation-assessment-pulse-2-1"
            type="checkbox"
            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            checked={primaryAssessment.circulationAssessment.pulse.quality === 'Weak'}
            onChange={() => handlePulseChange('quality', 'Weak')}
          />
          <span className="text-xs text-gray-700">Weak</span>
        </label>
        <label className="flex items-center space-x-2 p-1 hover:bg-gray-50 rounded" htmlFor="PrimaryAssessment-circulation-assessment-pulse-2-2">
          <input
            id="PrimaryAssessment-circulation-assessment-pulse-2-2"
            type="checkbox"
            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            checked={primaryAssessment.circulationAssessment.pulse.quality === 'Absent'}
            onChange={() => handlePulseChange('quality', 'Absent')}
          />
          <span className="text-xs text-gray-700">Absent</span>
        </label>
        <label className="flex items-center space-x-2 p-1 hover:bg-gray-50 rounded" htmlFor="PrimaryAssessment-circulation-assessment-pulse-2-3">
          <input
            id="PrimaryAssessment-circulation-assessment-pulse-2-3"
            type="checkbox"
            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            checked={primaryAssessment.circulationAssessment.pulse.quality === 'Bounding'}
            onChange={() => handlePulseChange('quality', 'Bounding')}
          />
          <span className="text-xs text-gray-700">Bounding</span>
        </label>
      </div>
    </div>
  </div>
</div>
            </div>
          </div>
          
         {/* GCS Section */}
<div ref={gcsRef} className={`bg-white p-4 rounded-lg shadow-sm ${sectionHighlight === 'gcs' ? 'ring-2 ring-blue-500' : ''}`}>
  <div className="flex justify-between items-center mb-1">
    <label htmlFor="PrimaryAssessment-gcs" className="block text-sm font-medium text-gray-700">
      GCS
    </label>
  </div>
  
  <div className="bg-gray-50 p-2 sm:p-4 rounded-lg">
    <div className="border rounded-lg overflow-hidden">
      {/* Header row */}
      <div className="grid grid-cols-1 md:grid-cols-2 bg-gray-200">
        <div className="md:border-r border-gray-300 px-3 py-2 md:px-4 md:py-3">
          <h5 className="text-base md:text-lg font-bold text-gray-800 text-center">Behavior</h5>
        </div>
        <div className="px-3 py-2 md:px-4 md:py-3">
          <h5 className="text-base md:text-lg font-bold text-gray-800 text-center">Response</h5>
        </div>
      </div>
      
      <div className="divide-y divide-gray-200">
        {/* Eye Opening Response */}
        <div className="grid grid-cols-1 md:grid-cols-2 bg-white">
          <div className="md:border-r border-gray-200 p-2 md:p-3 flex items-center justify-center">
            <p id="gcs-eye-opening-response" className="text-sm md:text-base font-medium text-gray-700 text-center">
              Eye Opening Response
            </p>
          </div>
          <div className="p-2 md:p-3">
  <div className="space-y-1">
    {/* Option 4 */}
    <label className="flex items-center space-x-2 p-1.5 rounded hover:bg-gray-50" htmlFor="PrimaryAssessment-gcs-0-0">
      <input
        id="PrimaryAssessment-gcs-0-0"
        type="checkbox"
        className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
        checked={primaryAssessment.GCS.eyeOpening === 4}
        onChange={() => handleGCSChange('eyeOpening', 4)}
      />
      <span className="text-xs sm:text-sm text-gray-700">4. Spontaneously</span>
    </label>
    
    {/* Option 3 */}
    <label className="flex items-center space-x-2 p-1.5 rounded hover:bg-gray-50" htmlFor="PrimaryAssessment-gcs-0-1">
      <input
        id="PrimaryAssessment-gcs-0-1"
        type="checkbox"
        className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
        checked={primaryAssessment.GCS.eyeOpening === 3}
        onChange={() => handleGCSChange('eyeOpening', 3)}
      />
      <span className="text-xs sm:text-sm text-gray-700">3. To speech</span>
    </label>
    
    {/* Option 2 */}
    <label className="flex items-center space-x-2 p-1.5 rounded hover:bg-gray-50" htmlFor="PrimaryAssessment-gcs-0-2">
      <input
        id="PrimaryAssessment-gcs-0-2"
        type="checkbox"
        className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
        checked={primaryAssessment.GCS.eyeOpening === 2}
        onChange={() => handleGCSChange('eyeOpening', 2)}
      />
      <span className="text-xs sm:text-sm text-gray-700">2. To pain</span>
    </label>
    
    {/* Option 1 */}
    <label className="flex items-center space-x-2 p-1.5 rounded hover:bg-gray-50" htmlFor="PrimaryAssessment-gcs-0-3">
      <input
        id="PrimaryAssessment-gcs-0-3"
        type="checkbox"
        className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
        checked={primaryAssessment.GCS.eyeOpening === 1}
        onChange={() => handleGCSChange('eyeOpening', 1)}
      />
      <span className="text-xs sm:text-sm text-gray-700">1. No response</span>
    </label>
  </div>
</div>
        </div>
        
        {/* Verbal Response */}
        <div className="grid grid-cols-1 md:grid-cols-2 bg-white">
          <div className="md:border-r border-gray-200 p-2 md:p-3 flex items-center justify-center">
            <p id="gcs-verbal-response" className="text-sm md:text-base font-medium text-gray-700 text-center">
              Verbal Response
            </p>
          </div>
          <div className="p-2 md:p-3">
            <div className="space-y-1">
              {/* Option 5 */}
              <label className="flex items-center space-x-2 p-1.5 rounded hover:bg-gray-50" htmlFor="PrimaryAssessment-gcs-1-0">
                <input
                  id="PrimaryAssessment-gcs-1-0"
                  type="checkbox"
                  className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  checked={primaryAssessment.GCS.verbalResponse === 5}
                  onChange={() => handleGCSChange('verbalResponse', 5)}
                />
                <span className="text-xs sm:text-sm text-gray-700">5. Oriented to time, person and place</span>
              </label>
              
              {/* Option 4 */}
              <label className="flex items-center space-x-2 p-1.5 rounded hover:bg-gray-50" htmlFor="PrimaryAssessment-gcs-1-1">
                <input
                  id="PrimaryAssessment-gcs-1-1"
                  type="checkbox"
                  className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  checked={primaryAssessment.GCS.verbalResponse === 4}
                  onChange={() => handleGCSChange('verbalResponse', 4)}
                />
                <span className="text-xs sm:text-sm text-gray-700">4. Confused</span>
              </label>
              
              {/* Option 3 */}
              <label className="flex items-center space-x-2 p-1.5 rounded hover:bg-gray-50" htmlFor="PrimaryAssessment-gcs-1-2">
                <input
                  id="PrimaryAssessment-gcs-1-2"
                  type="checkbox"
                  className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  checked={primaryAssessment.GCS.verbalResponse === 3}
                  onChange={() => handleGCSChange('verbalResponse', 3)}
                />
                <span className="text-xs sm:text-sm text-gray-700">3. Inappropriate words</span>
              </label>
              
              {/* Option 2 */}
              <label className="flex items-center space-x-2 p-1.5 rounded hover:bg-gray-50" htmlFor="PrimaryAssessment-gcs-1-3">
                <input
                  id="PrimaryAssessment-gcs-1-3"
                  type="checkbox"
                  className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  checked={primaryAssessment.GCS.verbalResponse === 2}
                  onChange={() => handleGCSChange('verbalResponse', 2)}
                />
                <span className="text-xs sm:text-sm text-gray-700">2. Incomprehensible sounds</span>
              </label>
              
              {/* Option 1 */}
              <label className="flex items-center space-x-2 p-1.5 rounded hover:bg-gray-50" htmlFor="PrimaryAssessment-gcs-1-4">
                <input
                  id="PrimaryAssessment-gcs-1-4"
                  type="checkbox"
                  className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  checked={primaryAssessment.GCS.verbalResponse === 1}
                  onChange={() => handleGCSChange('verbalResponse', 1)}
                />
                <span className="text-xs sm:text-sm text-gray-700">1. No response</span>
              </label>
            </div>
          </div>
        </div>
        
        {/* Motor Response */}
        <div className="grid grid-cols-1 md:grid-cols-2 bg-white">
          <div className="md:border-r border-gray-200 p-2 md:p-3 flex items-center justify-center">
            <p id="gcs-motor-response" className="text-sm md:text-base font-medium text-gray-700 text-center">
              Motor Response
            </p>
          </div>
          <div className="p-2 md:p-3">
            <div className="space-y-1">
              {/* Option 6 */}
              <label className="flex items-center space-x-2 p-1.5 rounded hover:bg-gray-50" htmlFor="PrimaryAssessment-gcs-2-0">
                <input
                  id="PrimaryAssessment-gcs-2-0"
                  type="checkbox"
                  className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  checked={primaryAssessment.GCS.motorResponse === 6}
                  onChange={() => handleGCSChange('motorResponse', 6)}
                />
                <span className="text-xs sm:text-sm text-gray-700">6. Obeys command</span>
              </label>
              
              {/* Option 5 */}
              <label className="flex items-center space-x-2 p-1.5 rounded hover:bg-gray-50" htmlFor="PrimaryAssessment-gcs-2-1">
                <input
                  id="PrimaryAssessment-gcs-2-1"
                  type="checkbox"
                  className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  checked={primaryAssessment.GCS.motorResponse === 5}
                  onChange={() => handleGCSChange('motorResponse', 5)}
                />
                <span className="text-xs sm:text-sm text-gray-700">5. Moves to localised pain</span>
              </label>
              
              {/* Option 4 */}
              <label className="flex items-center space-x-2 p-1.5 rounded hover:bg-gray-50" htmlFor="PrimaryAssessment-gcs-2-2">
                <input
                  id="PrimaryAssessment-gcs-2-2"
                  type="checkbox"
                  className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  checked={primaryAssessment.GCS.motorResponse === 4}
                  onChange={() => handleGCSChange('motorResponse', 4)}
                />
                <span className="text-xs sm:text-sm text-gray-700">4. Withdraws from pain</span>
              </label>
              
              {/* Option 3 */}
              <label className="flex items-center space-x-2 p-1.5 rounded hover:bg-gray-50" htmlFor="PrimaryAssessment-gcs-2-3">
                <input
                  id="PrimaryAssessment-gcs-2-3"
                  type="checkbox"
                  className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  checked={primaryAssessment.GCS.motorResponse === 3}
                  onChange={() => handleGCSChange('motorResponse', 3)}
                />
                <span className="text-xs sm:text-sm text-gray-700">3. Abnormal flexion</span>
              </label>
              
              {/* Option 2 */}
              <label className="flex items-center space-x-2 p-1.5 rounded hover:bg-gray-50" htmlFor="PrimaryAssessment-gcs-2-4">
                <input
                  id="PrimaryAssessment-gcs-2-4"
                  type="checkbox"
                  className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  checked={primaryAssessment.GCS.motorResponse === 2}
                  onChange={() => handleGCSChange('motorResponse', 2)}
                />
                <span className="text-xs sm:text-sm text-gray-700">2. Abnormal extension</span>
              </label>
              
              {/* Option 1 */}
              <label className="flex items-center space-x-2 p-1.5 rounded hover:bg-gray-50" htmlFor="PrimaryAssessment-gcs-2-5">
                <input
                  id="PrimaryAssessment-gcs-2-5"
                  type="checkbox"
                  className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  checked={primaryAssessment.GCS.motorResponse === 1}
                  onChange={() => handleGCSChange('motorResponse', 1)}
                />
                <span className="text-xs sm:text-sm text-gray-700">1. No response</span>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    {/* Total Score */}
    <div className="mt-4 flex items-center">
      <label htmlFor="PrimaryAssessment-gcs-total-score" className="text-sm font-medium text-gray-700 mr-2">
        Total Score:
      </label>
      <input
        id="PrimaryAssessment-gcs-total-score"
        type="text"
        className="w-16 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
        value={primaryAssessment.GCS.totalScore}
        readOnly
      />
    </div>
  </div>
</div>
          
          <div ref={patientPriorityRef} className={`${sectionHighlight === 'patient-priority' ? 'ring-2 ring-blue-500' : ''}`}>
            <TextareaField
              id="PrimaryAssessment-decision-(pt.-priority)"
              label="Decision (Pt. Priority)"
              value={primaryAssessment.patientPriority}
              onChange={(e) => handleTextChange('patientPriority', e.target.value)}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrimaryAssessment;